import React, { createContext, useCallback, useContext, useState } from 'react';
import useQueryPretaxAccounts from 'hooks/useQueryPretaxAccounts';
import useQueryPretaxDependents from 'hooks/useQueryPretaxDependents';
import useQueryPretaxV2Dependent from 'hooks/useQueryPretaxV2Dependents';

import CurrentUserContext from 'contexts/CurrentUserContext';
import {
  ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT,
  PRETAX_COMMUTER_ENROLL_STATUS,
} from 'constants/accounts';
import { findIndex } from 'lodash';

import {
  PretaxAccount,
  PretaxV2Dependent,
  EmployeeWallet,
  IPretaxContext,
} from './types';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { FEATURE_FLAGS } from 'constants/featureFlag';
import { isPretaxWallet, isHSATypeAccount } from 'utils/pretaxAccount';
import useQueryPretaxEligibleWallets from 'hooks/useQueryPretaxEligibleWallets';

const PretaxContext = createContext<IPretaxContext>({
  alegeus_pretax_accounts: [],
  alegeus_pretax_dependents: [],
  is_loading_pretax: true,
  new_pretax_experience: false,
  pretax_accounts: [],
  pretax_dependents: [],
  formaCommuterEligible: false,
  pretaxEligibleWallets: [],
  updateWallets: () => {},
  refetchPretaxV1Dependents: () => {},
  isAlegeusHSA: false,
});

export const PretaxContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    employee: {
      settings: {
        is_cdh_enabled,
        is_pretax_enabled,
        pretax_commuter_status = PRETAX_COMMUTER_ENROLL_STATUS.SELECTION_DISABLED,
      },
      impersonated,
    },
    company: {
      features: { pretax_config = { twic_commuter: false } },
    },
  } = useContext(CurrentUserContext);

  const feature_flag = useContext(FeatureFlagContext);
  const pretax_v2_enabled: boolean = feature_flag.flag(
    FEATURE_FLAGS.FORMA_PRETAX_ACCOUNTS_CLAIMS_PAGE,
    false
  );

  const isAlegeusHSAEnabled = feature_flag.flag(
    FEATURE_FLAGS.ALEGEUS_HSA_ENABLED,
    false
  );

  const [employee_wallets, setEmployeeWallets] = useState<
    Array<EmployeeWallet>
  >([]);

  const new_pretax_experience: boolean = impersonated
    ? localStorage.getItem('pretax_v2') === 'true'
    : pretax_v2_enabled;

  const {
    data: alegeus_pretax_accounts_data,
    is_loading: is_pretax_accounts_loading,
  } = useQueryPretaxAccounts({
    is_cdh_enabled:
      (is_cdh_enabled && !new_pretax_experience) || isAlegeusHSAEnabled,
  });

  const {
    data: pretax_dependents_data,
    is_loading: is_pretax_dependents_loading,
    refetch: refetchPretaxV1Dependents,
  } = useQueryPretaxDependents({
    is_cdh_enabled: is_cdh_enabled && !new_pretax_experience,
  });

  const updateEmployeeWallets = useCallback(
    (employee_wallets: Array<EmployeeWallet>) => {
      setEmployeeWallets(employee_wallets);
    },
    []
  );

  const { data: pretaxv2_dependents_data } = useQueryPretaxV2Dependent({
    is_pretax_enabled: new_pretax_experience,
  });

  const formaCommuterEligible = Boolean(
    is_pretax_enabled &&
      pretax_commuter_status !==
        PRETAX_COMMUTER_ENROLL_STATUS.SELECTION_DISABLED
  );

  // Right now we only support commuter accounts, so enable this query
  // when user is commuter eligible for performance purpose.
  const { data: eligibleWalletsData } = useQueryPretaxEligibleWallets({
    enabled: formaCommuterEligible && new_pretax_experience,
    formaEnrollment: true,
  });

  if (new_pretax_experience) {
    /*
     * Phase 1 (Now - 12/31/2022) - PRT-2330 - Feature Flag: ALEGEUS_HSA_ENABLED
     * If User has Alegeus HSA only, show old Account List Item for that HSA
     * If User has Forma HSA only, prevent user from seeing it
     * If User has both, show Alegeus HSA information with old Account List Item component
     */
    const alegeus_pretax_accounts =
      alegeus_pretax_accounts_data?.pretax_accounts ?? [];

    const alegeusHSA = alegeus_pretax_accounts.filter((account) =>
      isHSATypeAccount(account.account_type)
    );

    /*
     * Phase 2 (1/1/2023 - 3/2/2023) - PRT-2330 - Feature Flag: ALEGEUS_HSA_ENABLED
     * Add check for if user only has Alegeus HSA,
     *   modify old AccountListItem where clicking it will direct user straight to Alegeus.
     * Allow Forma HSA to flow through in useQueryProfile.ts
     * So User will see Alegeus and Forma HSA in the AccountsList
     */
    const pretax_accounts = employee_wallets.filter(isPretaxWallet) || [];
    const pretax_dependents: Array<PretaxV2Dependent> =
      pretaxv2_dependents_data?.pretax_dependents || [];

    return (
      <PretaxContext.Provider
        value={{
          pretax_accounts,
          pretax_dependents,
          is_loading_pretax: is_pretax_accounts_loading,
          new_pretax_experience: true,
          alegeus_pretax_accounts: alegeusHSA,
          alegeus_pretax_dependents: [],
          formaCommuterEligible,
          pretaxEligibleWallets: eligibleWalletsData?.eligibleWallets || [],
          updateWallets: updateEmployeeWallets,
          isAlegeusHSA: isAlegeusHSAEnabled,
        }}
      >
        {children}
      </PretaxContext.Provider>
    );
  }

  const alegeus_pretax_accounts =
    alegeus_pretax_accounts_data?.pretax_accounts || [];
  const alegeus_pretax_dependents =
    pretax_dependents_data?.pretax_dependents || [];
  const commuter_pending_enroll =
    pretax_config.twic_commuter &&
    pretax_commuter_status === PRETAX_COMMUTER_ENROLL_STATUS.SELECTION_PENDING;
  if (
    commuter_pending_enroll &&
    findIndex(
      alegeus_pretax_accounts,
      (a: PretaxAccount) =>
        a.flex_account_id ===
        ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT.flex_account_id
    ) === -1
  ) {
    alegeus_pretax_accounts.push(
      ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT as PretaxAccount
    );
  }

  return (
    <PretaxContext.Provider
      value={{
        alegeus_pretax_accounts,
        alegeus_pretax_dependents,
        is_loading_pretax:
          is_pretax_accounts_loading || is_pretax_dependents_loading,
        new_pretax_experience: false,
        pretax_accounts: [],
        pretax_dependents: [],
        formaCommuterEligible: false,
        pretaxEligibleWallets: [],
        updateWallets: () => {},
        refetchPretaxV1Dependents,
        // Doesn't affect old experience
        isAlegeusHSA: false,
      }}
    >
      {children}
    </PretaxContext.Provider>
  );
};

export default PretaxContext;
